import { Control, useWatch } from 'react-hook-form';
import { FinancesDrawerFormValues, SalesAidValues } from '../RightDrawerSchema';
import { OptionItem, SalesAidView, SubsidyRecipientView } from '../../../../../generated/ApiClient';
import {
  Box,
  Collapse,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Switch,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../../../common/auth/PermissionsContext';
import { FormCheckbox } from '../../../../../component/form/FormCheckbox';
import { SalesAidOverview } from './salesAids/SalesAidOverview';

interface Props {
  control: Control<FinancesDrawerFormValues>;
  loading: boolean;
  onRelevantChange: (name: string, value: unknown) => void;
  specialOffers: SalesAidView[];
  dealSalesAids: (SalesAidValues | SalesAidView)[];
  onAddSalesAid: (val: SalesAidValues) => void;
  onEditSalesAid: (salesAid: SalesAidValues, index: number) => void;
  givers: OptionItem[];
  recipients: SubsidyRecipientView[];
  recipientUnits: { [index: number]: string };
  readonly?: boolean;
}

export const FinancesCalculationPromotions = ({
  control,
  loading,
  onRelevantChange,
  specialOffers,
  dealSalesAids,
  onAddSalesAid,
  onEditSalesAid,
  givers,
  recipients,
  recipientUnits,
  readonly,
}: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  const canViewLocked = hasPermission('LockSalesAid', 'Execute');
  const specialOfferId = useWatch({ control, name: 'specialOfferId' });
  const selDealSalesAid = useWatch({ control, name: 'dealSalesAidId' });

  const isBundle = useWatch({ control, name: 'isBundle' });

  const relevantSpecialOffers = specialOffers.filter((x) => x.isBundle === isBundle);
  // Deal sales aids include copies of bundles (isBundle === true). Used for the count label
  // only; the radio list is rendered from the FULL dealSalesAids array and filtered per-item
  // inside SalesAidOverview so its indices stay aligned with dealSalesAidId (an index into the
  // full array). SalesAidValues (locally added, not yet saved) carry no isBundle flag.
  const relevantSalesAids = dealSalesAids.filter(
    (x) => ('isBundle' in x ? x.isBundle : false) === isBundle
  );

  const defaultOpen = Boolean(specialOfferId || selDealSalesAid != null);

  const [open, setOpen] = useState(defaultOpen);
  const [switchChecked, setSwitchChecked] = useState(defaultOpen);

  const showBundle =
    specialOffers.some((x) => x.isBundle) ||
    dealSalesAids.some((x) => 'isBundle' in x && x.isBundle);

  useEffect(() => {
    if (isBundle || specialOfferId != null || selDealSalesAid != null) {
      setOpen(true);
      setSwitchChecked(true);
    } else {
      setOpen(false);
      setSwitchChecked(false);
    }
  }, [specialOfferId, selDealSalesAid, isBundle]);

  const handleClickOpen = (event: any) => {
    event.preventDefault();
    setOpen(!open);
  };

  const handleSwitchChecked = (event: any) => {
    if (!open && !switchChecked) {
      setSwitchChecked(event.target.checked);
      onRelevantChange('specialOfferEnabled', !open);
      setOpen(!open);
    } else if (!switchChecked && open) {
      setSwitchChecked(event.target.checked);
      onRelevantChange('specialOfferEnabled', open);
    } else if (open && switchChecked) {
      setSwitchChecked(event.target.checked);
      onRelevantChange('specialOfferEnabled', !open);
      setOpen(!open);
    } else {
      setSwitchChecked(event.target.checked);
      setOpen(false);
    }
  };

  return (
    <Box>
      <List>
        <ListItemButton disabled={readonly}>
          <ListItemText
            primary={
              <FormControlLabel
                onChange={handleSwitchChecked}
                control={<Switch disabled={readonly} checked={switchChecked} />}
                label={t('deals.new.financesConfiguration.calculationDrawer.promotions', {
                  value: [...relevantSalesAids, ...relevantSpecialOffers].filter(
                    (x) => !x.isLocked || canViewLocked
                  ).length,
                })}
              />
            }
          />
          {open ? (
            <ExpandLess onClick={handleClickOpen} />
          ) : (
            <ExpandMore onClick={handleClickOpen} />
          )}
        </ListItemButton>
        {showBundle && (
          <ListItem>
            <FormCheckbox
              variant="switch"
              control={control}
              label={t('deals.new.financesConfiguration.calculationDrawer.isBundle')}
              name="isBundle"
              disabled={readonly || loading}
              onChange={(evt) => onRelevantChange('isBundle', evt.target.checked)}
            />
          </ListItem>
        )}
      </List>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <SalesAidOverview
          control={control}
          loading={loading}
          onRelevantChange={onRelevantChange}
          specialOffers={relevantSpecialOffers}
          dealSalesAids={dealSalesAids}
          onAddSalesAid={onAddSalesAid}
          onEditSalesAid={onEditSalesAid}
          givers={givers}
          recipients={recipients}
          recipientUnits={recipientUnits}
          readonly={readonly}
          isBundle={isBundle}
        />
      </Collapse>
    </Box>
  );
};
